import { Plus } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { IS_APPLE } from './is-apple';

/**
 * Single source for the combo shown on the button — swap the shortcut here
 * and every render of the badge follows. The window keydown handler that
 * actually fires it lives in `chat-top-bar.tsx` — keep the two in sync if
 * the combo ever changes.
 *
 * ⌥N rather than the design's ⌘N: browsers reserve Cmd/Ctrl+N for "new
 * window" and never deliver it to the page, so that badge would be a lie
 * (design doc decision #6 — the badge must never show a combo that does
 * nothing).
 */
export const NEW_CHAT_SHORTCUT_LABEL = IS_APPLE ? '⌥N' : 'Alt+N';

interface NewChatButtonProps {
  onClick: () => void;
  /** `sm` is the activity board footer's compact variant — 30px, no
   *  shortcut badge (README §"Chat activity board" footer). */
  size?: 'default' | 'sm';
  className?: string;
}

/**
 * The most prominent element in the bar, by intent (README §1). Brand-blue
 * fill, a plus icon, and — at default size — the keyboard-shortcut badge.
 */
export function NewChatButton({ onClick, size = 'default', className }: NewChatButtonProps) {
  if (size === 'sm') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex h-[30px] shrink-0 items-center gap-[7px] rounded-[9px] bg-[var(--eddy-blue)] px-[11px]',
          'text-[13px] font-medium whitespace-nowrap text-white transition-colors hover:bg-[var(--eddy-blue-hover)]',
          className,
        )}
      >
        <Plus size={12} />
        New chat
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // 38 matches the status cluster and the ask chip exactly, so the
        // whole left group reads as one row.
        'eddy-chat-btn inline-flex h-[38px] shrink-0 items-center gap-[10px] rounded-[11px] bg-[var(--eddy-blue)] px-[18px]',
        'text-[16px] font-semibold tracking-[-0.015em] whitespace-nowrap text-white',
        'shadow-[0_5px_14px_-6px_rgba(41,98,255,0.7)] transition-[background-color,box-shadow] duration-150',
        'hover:bg-[var(--eddy-blue-hover)] hover:shadow-[0_8px_20px_-8px_rgba(41,98,255,0.85)]',
        className,
      )}
    >
      <Plus size={15} />
      {/* "Chat", not "New chat": the plus glyph already says "new", and the
       *  button sits beside a "Build" one. The compact `sm` variant in the
       *  board footer deliberately KEEPS "New chat": it sits under a list of
       *  existing chats, where a bare "Chat" would be ambiguous. */}
      Chat
      <span className="eddy-kbd rounded-[6px] border border-white/25 bg-white/20 px-[6px] py-[2px] font-mono text-xs leading-tight font-medium text-white">
        {NEW_CHAT_SHORTCUT_LABEL}
      </span>
    </button>
  );
}
